import { type RefObject, useLayoutEffect, useRef, useState } from 'react'

/**
 * The rendered width (CSS px) of the element behind the returned ref, tracked with a
 * ResizeObserver: charts draw at their real width instead of scaling a fixed viewBox, so they fill
 * wide layouts without blowing up their labels. `fallback` until measured (and in jsdom).
 */
export function useElementWidth<T extends Element>(fallback: number, min = 0): [RefObject<T>, number] {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(fallback)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return undefined
    const measure = (w: number) => {
      if (w > 0) setWidth(Math.max(min, Math.round(w)))
    }
    measure(el.getBoundingClientRect().width)
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) measure(entry.contentRect.width)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [min])
  return [ref, width]
}
